import { isHealthDoc, isMaskFix, MASK_FIX_LABEL } from '../../lib/healthMask';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { DocumentPreview } from '../documents/DocumentPreview';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Loader2, FileX, Download, History, CheckCircle2, Scissors, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { confirmDialog } from '../ConfirmHost';
import { DependencyRuleNote } from '../documents/docDependencies';
import { SplitDocumentDialog } from '../documents/SplitDocumentDialog';
import { PageCountBadge } from '../documents/PageCountBadge';
import { formatDate, getActualDocStatus } from '../../lib/helpers';
import { DocumentUploadButton } from './DocumentUploadButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  onay: { label: 'Onaylı', cls: 'bg-emerald-100 text-emerald-700' },
  eksik: { label: 'Eksik', cls: 'bg-rose-100 text-rose-700' },
  beklemede: { label: 'Beklemede', cls: 'bg-amber-100 text-amber-700' },
  suresi_doldu: { label: 'Süresi Doldu', cls: 'bg-orange-100 text-orange-700' },
  kapsam_disi: { label: 'Kapsam Dışı', cls: 'bg-slate-100 text-slate-600' },
};

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

// En son karar önde ve renkli, önceki karar soluk gösterilir
const ReviewTrail = ({ f, testId }) => {
  const approved = f.approved_at && <p key="a" className="text-emerald-700">Onaylayan: {f.approved_by || '-'} · {fmtDateTime(f.approved_at)}</p>;
  const rejected = f.rejected_at && (
    <div key="r" className="text-rose-700">
      <p>Reddeden: {f.rejected_by || '-'} · {fmtDateTime(f.rejected_at)}</p>
      {f.rejection_note && <p className="text-rose-600">Neden: {f.rejection_note}</p>}
    </div>
  );
  const rejectedLast = f.rejected_at && (!f.approved_at || f.rejected_at > f.approved_at);
  const [latest, older] = rejectedLast ? [rejected, approved] : [approved, rejected];
  return (
    <div className="space-y-0.5" data-testid={testId}>
      {latest}
      {older && <div className="opacity-50" data-testid={`${testId}-older`}>{older}</div>}
    </div>
  );
};

const VersionHistory = ({ history, activeId, onSelect }) => (
  <div className="rounded-lg border p-3" data-testid="document-viewer-history">
    <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1"><History className="h-3 w-3" /> Belge Geçmişi ({history.length})</p>
    <div className="space-y-1 max-h-56 overflow-y-auto">
      {history.map((f, i) => (
        <button
          key={f.id}
          type="button"
          onClick={() => onSelect(f)}
          className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${f.id === activeId ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-slate-50'}`}
          data-testid={`document-viewer-history-item-${i}`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-slate-700">v{history.length - i} · {formatDate(f.uploaded_at?.slice(0, 10))}</span>
            {f.rejected_at && (!f.approved_at || f.rejected_at > f.approved_at)
              ? <span className="text-rose-600">Reddedildi</span>
              : f.approved_at ? <span className="text-emerald-600 flex items-center gap-0.5"><CheckCircle2 className="h-3 w-3" /> Onaylı</span> : <span className="text-amber-600">Onaysız</span>}
          </div>
          <p className="text-slate-400 truncate">{f.original_filename}</p>
          <p className="text-slate-400">Yükleyen: {f.uploaded_by || '-'} · {fmtDateTime(f.uploaded_at)}</p>
          <ReviewTrail f={f} testId={`document-viewer-history-trail-${i}`} />
        </button>
      ))}
    </div>
  </div>
);

// mode: 'approved' -> son onaylı dosya (yoksa son yüklenen), 'latest' -> son yüklenen
// Firma: belgeyi (onaylı / onay bekleyen / eksik) siler; belge "Yüklenmedi"ye döner, bağlı onaylar düşer
const ViewerDeleteButton = ({ entityType, entityId, docKey, docLabel, onDeleted }) => {
  const remove = async () => {
    if (!(await confirmDialog({ title: 'Belge silinsin mi?', description: <>{docLabel} belgesi silinecek; yeniden yükleyip onaya göndermeniz gerekir.<DependencyRuleNote docKey={docKey} /></>, confirmLabel: 'Evet, Sil' }))) return;
    try {
      const res = await axios.delete(`${API}/${entityType}/${entityId}/document-files/${docKey}`);
      toast.success('Belge silindi, yeniden yükleyebilirsiniz');
      onDeleted(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Belge silinemedi'); }
  };
  return (
    <button type="button" onClick={remove} data-testid="document-viewer-delete-btn"
      className="flex w-full items-center justify-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 transition-colors hover:bg-rose-100">
      <Trash2 className="h-4 w-4" />Belgeyi Sil
    </button>
  );
};

export const DocumentViewerDialog = ({ open, onOpenChange, entity: employee, entityType = 'employees', subtitle, docKey, docLabel, mode = 'approved', onUploaded, onReplaced, onDeleted, decisionPanel, extraInfo, compact = false }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [splitOpen, setSplitOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);
  const docs = employee?.documents || {};
  const approvedFile = employee?.approved_document_files?.[docKey];
  const latestFile = employee?.document_files?.[docKey];
  // İSG karar ekranında (decisionPanel) her zaman son yüklenen dosya açılır; eski sürümler yalnızca görüntülenir
  const useApproved = !selected && mode === 'approved' && approvedFile && !decisionPanel;
  const fileInfo = selected || (useApproved ? approvedFile : latestFile);

  useEffect(() => {
    setSelected(null);
    setHistory([]);
    if (!open || !employee?.id || !docKey) return;
    axios.get(`${API}/${entityType}/${employee.id}/document-files/${docKey}/history`)
      .then((res) => setHistory(res.data))
      .catch(() => setHistory([]));
  }, [open, entityType, employee?.id, docKey, latestFile?.id]);

  useEffect(() => {
    if (!open || !fileInfo) return undefined;
    let url = null;
    setLoading(true);
    axios.get(`${API}/${entityType}/${employee.id}/document-files/${docKey}`, {
      params: selected ? { file_id: selected.id } : employee._snapshot ? { file_id: latestFile.id } : { which: useApproved ? 'approved' : 'latest' },
      responseType: 'blob',
    })
      .then((res) => { url = URL.createObjectURL(res.data); setBlobUrl(url); })
      .catch(() => setBlobUrl(null))
      .finally(() => setLoading(false));
    return () => { if (url) URL.revokeObjectURL(url); setBlobUrl(null); };
  }, [open, entityType, employee?.id, docKey, fileInfo?.id, useApproved, selected]);

  if (!employee) return null;
  const fileRecord = history.find((h) => h.id === fileInfo?.id) || fileInfo;
  const isPendingFile = fileRecord && !fileRecord.approved_at && !fileRecord.rejected_at;
  const status = getActualDocStatus(docs, docKey);
  const maskFix = isHealthDoc(entityType, docKey) && status === 'eksik' && isMaskFix(docs[`${docKey}_note`]);
  const st = maskFix ? { label: MASK_FIX_LABEL, cls: 'bg-slate-900 text-white' } : (STATUS_STYLES[status] || STATUS_STYLES.beklemede);
  const isMyk = docKey === 'mesleki_yeterlilik';
  const validity = isMyk ? (docs.mesleki_yeterlilik_validity || docs.mesleki_yeterlilik_date) : docs[`${docKey}_validity`];
  const date = isMyk ? null : docs[`${docKey}_date`];
  const note = docs[`${docKey}_note`];
  const isOldVersion = !!decisionPanel && !!fileInfo && !!latestFile && fileInfo.id !== latestFile.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[94vh] overflow-y-auto" data-testid="document-viewer-dialog" data-allow-pending aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span>{docLabel}</span>
            <span className="text-sm font-normal text-slate-500">{subtitle}</span>
            <Badge className={st.cls} data-testid="document-viewer-status">{st.label}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-[1fr_260px] gap-5">
          <div className="min-w-0 bg-slate-50 rounded-lg border min-h-[420px] flex items-center justify-center overflow-hidden" data-testid="document-viewer-preview">
            {!fileInfo && (
              <div className="text-center text-slate-400 p-8">
                <FileX className="h-10 w-10 mx-auto mb-2" />
                <p className="text-sm">Bu belge için yüklenmiş dosya bulunmuyor</p>
              </div>
            )}
            {fileInfo && loading && <Loader2 className="h-8 w-8 animate-spin text-slate-400" />}
            {fileInfo && !loading && blobUrl && (
              <DocumentPreview key={blobUrl} url={blobUrl} contentType={fileInfo.content_type} filename={fileInfo.original_filename} alt={docLabel} />
            )}
            {fileInfo && !loading && !blobUrl && <p className="text-sm text-rose-500">Dosya yüklenemedi</p>}
          </div>
          <div className="space-y-4">
            {extraInfo}
            {fileInfo && !compact && (
              <div className="rounded-lg border p-3 text-xs text-slate-500 space-y-1">
                {isPendingFile && <p className="text-amber-600 font-medium" data-testid="document-viewer-pending-note">Bu dosya henüz onaylanmadı</p>}
                <p className="truncate" title={fileInfo.original_filename}>{fileInfo.original_filename}</p>
                <PageCountBadge info={fileRecord} testId="document-viewer-pages" />
                {fileRecord?.kvkk_masked && (
                  <p className="font-medium text-emerald-700" data-testid="document-viewer-kvkk-badge">{fileRecord.mask_mode === 'not_needed' ? 'KVKK: maskeleme gerekmediği beyan edildi' : 'KVKK maskeli (kalıcı)'}</p>
                )}
                <p>Yükleme: {fmtDateTime(fileInfo.uploaded_at)}{fileInfo.uploaded_by ? ` · ${fileInfo.uploaded_by}` : ''}</p>
                <ReviewTrail f={fileRecord} testId="document-viewer-review-trail" />
                {blobUrl && (
                  <a href={blobUrl} download={fileInfo.original_filename} className="inline-flex items-center gap-1 text-blue-600 hover:underline pt-1" data-testid="document-viewer-download">
                    <Download className="h-3 w-3" /> İndir
                  </a>
                )}
              </div>
            )}
            {isOldVersion && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2 text-xs text-amber-800" data-testid="document-viewer-old-version">
                <p><b>Eski sürüm görüntüleniyor.</b> Bu sürüm üzerinde işlem yapılamaz; onay/red yalnızca son yüklenen belge için yapılır.</p>
                <button type="button" onClick={() => setSelected(null)} className="font-medium text-blue-700 hover:underline" data-testid="document-viewer-back-latest">Son yüklenen belgeye dön</button>
              </div>
            )}
            {onReplaced && fileInfo && !isOldVersion && blobUrl && !loading && (fileInfo.content_type === 'application/pdf' || fileInfo.content_type?.startsWith('image/')) && (
              <button type="button" onClick={() => setSplitOpen(true)} data-testid="document-viewer-split-btn"
                className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-sky-300 bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-800 transition-colors hover:bg-sky-100">
                <Scissors className="h-4 w-4" />{fileInfo.content_type === 'application/pdf' ? 'Sayfa Ayır' : 'Kırp'} (birleşik belge)
              </button>
            )}
            {fileInfo && !isOldVersion && decisionPanel}
            {onUploaded && !isOldVersion && (
              <div className="rounded-lg border border-blue-100 bg-blue-50/50 p-3 space-y-2" data-testid="document-viewer-upload">
                <p className="text-xs text-slate-600">Firma adına belge yükleyin. Yüklediğiniz belge otomatik onaylanır.</p>
                <div className="flex">
                  <DocumentUploadButton entityType={entityType} entityId={employee.id} docKey={docKey} hasFile={!!latestFile} onUploaded={onUploaded} autoApprove />
                </div>
              </div>
            )}
            {onDeleted && latestFile && !isOldVersion && <ViewerDeleteButton entityType={entityType} entityId={employee.id} docKey={docKey} docLabel={docLabel} onDeleted={onDeleted} />}
            {!compact && history.length > 0 && (
              <VersionHistory history={history} activeId={fileInfo?.id} onSelect={setSelected} />
            )}
            {!compact && status === 'eksik' && note && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700" data-testid="document-viewer-note">
                {note}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
      {splitOpen && (
        <SplitDocumentDialog open onClose={() => setSplitOpen(false)} url={blobUrl} contentType={fileInfo?.content_type}
          entityType={entityType} entityId={employee.id} docKey={docKey} fileId={fileInfo?.id} docLabel={docLabel}
          onDone={(info) => { setSelected(null); onReplaced(info); }} />
      )}
    </Dialog>
  );
};
